import * as React from 'react';

// Material-UI
import Button from '@mui/material/Button';
import ClearIcon from '@mui/icons-material/Clear';
import { blue } from '@mui/material/colors';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip';
import { Typography } from '@mui/material';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  HTTPHeaders
} from '../../interfaces';

import {
  DateRange,
  PriorityPeriod
} from '../../models';

import {
  API_URL,
  DateFilterYear,
  EMPTY_STRING,
  REQUEST_FILTER_MAX_INTERVAL
} from '../../global';

import PriorityPeriodList from '../../shared/PriorityPeriodList';
import DateRangeFilter from '../../shared/DateRangeFilter';
import DateRangeFilterMobile from '../../shared/DateRangeFilterMobile';

// Hook for signup related dates
import { useSystem } from '../../hooks/UseSystem';
// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';
// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';

interface LodgeUseFilterProps {
  clearFilter: boolean;
  dateRange: DateRange;
  handleFilterChanged: (dateRange: DateRange) => void;
  disableActions: boolean;
  handleClear: () => void;
  handleRefresh: () => void;
}

const LodgeUseFilter: React.FC<LodgeUseFilterProps> = (props) => {

  const { getAccessToken } = useAuth0User();
  const { getCurrentFiscalYear, getFiscalYearStartDate, getFiscalYearEndDate } = useSystem();
  const { isMobile } = useResponsive();

  const [fiscalYears, setFiscalYears] = React.useState<Array<number>>([]);
  const [fiscalYear, setFiscalYear] = React.useState<string>(EMPTY_STRING);
  const [priorityPeriods, setPriorityPeriods] = React.useState<Array<PriorityPeriod>>([]);

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      margin: theme.spacing(0, 1, 0, 1),
      padding: theme.spacing(1, 0, 1, 0),
      backgroundColor: blue[50]
    },
    fiscalYear: {
      margin: theme.spacing(0, 2, 0, 2)
    },
    fiscalYearContainer: {
      borderRight: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(0, 0, 0, 2)
    },
    priorityperiods: {
      borderRight: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0, 2, 0, 0)
    },
  };

  // #endregion

  // #region Methods

  /*
* Fetch list of fiscal years
*/
  const fetchFiscalYears = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const params = new URLSearchParams();
    params.append('year', fiscalYear);

    const url = new URL(`${API_URL}/v1/requests/fiscalyears`);
    url.search = params.toString();

    await fetch(url.toString(),
      {
        method: 'GET'
        , cache: 'no-cache'
        , headers: headers
        , mode: 'cors'
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Fiscal years not retrieved');
        }
        return response.json()
      })
      .then((f: Array<number>) => {
        setFiscalYears(f);
      })
      .catch(err => {
        throw new Error('Fiscal years not retrieved');
      });
  }

  /*
  * Fiscal year label
  */
  const fiscalYearLabel = () => {
    return `5/1/${(Number(fiscalYear) - 1).toFixed(0)} - 4 /30/${fiscalYear}`;
  }

  /*
 * Get list of priority periods
 */
  const fetchPeriods = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const params = new URLSearchParams();
    params.append('startDate', getFiscalYearStartDate(Number(fiscalYear)));
    params.append('endDate', getFiscalYearEndDate(Number(fiscalYear)));

    const url = new URL(`${API_URL}/v1/requests/priorityperiods`);
    url.search = params.toString();

    await fetch(url.toString(), {
      method: 'GET'
      , cache: 'no-cache'
      , headers: headers
      , mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Priority periods were not retrieved.');
        }
        return response.json()
      })
      .then((periods: Array<PriorityPeriod>) => {
        setPriorityPeriods(periods);
      })
      .catch(err => {
        throw new Error('Priority periods were not retrieved.');
      }
      );
  }

  // #endregion

  // #region Event handlers

  /*
   * Handle changes to date filter
   */
  const handleFilter = (dateRange: DateRange) => {
    props.handleFilterChanged(dateRange);
  }

  /*
   * Handle fiscal year change
   */
  const handleFiscalYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFiscalYear(event.target.value as string);
  };

  /*
   * Handler for change in selected weekend
   */
  const handlePriorityPeriodChange = (period: PriorityPeriod) => {
    const d = new DateRange(period.arrivalDate, period.departureDate);
    props.handleFilterChanged(d);
  }

  /*
   * Handle changes to date filter
   */
  const handleClear = () => {
    props.handleClear();
  }

  React.useEffect(() => {
    if (fiscalYear !== EMPTY_STRING) {
      fetchPeriods();
      props.handleClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalYear]);

  React.useEffect(() => {
    if (fiscalYears.length > 0) {
      console.log(getCurrentFiscalYear());
      setFiscalYear(getCurrentFiscalYear());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalYears.length]);

  React.useEffect(() => {
    fetchFiscalYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #endregion

  return (
    <Grid container spacing={1} alignItems="flex-end" sx={styles.root}>
      {!isMobile &&
        <Grid>
          <Typography variant="overline">{fiscalYearLabel()}</Typography>
        </Grid>
      }
      {!isMobile &&
        <Grid sx={styles.fiscalYearContainer}>
          <TextField
            select
            label='Fiscal Year'
            sx={styles.fiscalYear}
            onChange={handleFiscalYearChange}
            value={fiscalYear}
          >
            {fiscalYears.map((gt: number) => (
              <MenuItem key={gt} value={gt}>{gt}</MenuItem>
            ))}
          </TextField>
        </Grid>
      }
      <Grid sx={styles.priorityperiods}>
        <PriorityPeriodList
          disabled={false}
          handlePriorityPeriodChange={handlePriorityPeriodChange}
          priorityPeriods={priorityPeriods}
          reset={props.clearFilter}
        />
      </Grid>
      {!isMobile &&
        <Grid>
          <DateRangeFilter
            clearDateRange={props.clearFilter}
            disabled={false}
            dateRange={props.dateRange}
            filterYear={DateFilterYear.RequestYear}
            handleChange={handleFilter}
            isArrivalDeparture={true}
            maxInterval={REQUEST_FILTER_MAX_INTERVAL}
          />
        </Grid>
      }
      {isMobile &&
        <Grid>
          <DateRangeFilterMobile
            clearDateRange={props.clearFilter}
            disabled={false}
            dateRange={props.dateRange}
            filterYear={DateFilterYear.RequestYear}
            handleChange={handleFilter}
            isArrivalDeparture={true}
            maxInterval={REQUEST_FILTER_MAX_INTERVAL}
          />
        </Grid>
      }
      <Grid>
        <Tooltip title="Clear filter and requests information">
          <span>
            <Button variant="outlined" color="primary" disabled={props.disableActions} onClick={handleClear}>
              <ClearIcon />
            </Button>
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  );

}

export default LodgeUseFilter;